import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import type { FinInvoiceDetail } from '@/types/finance';

type InvoiceSendModalProps = {
  open: boolean;
  onClose: () => void;
  /** Saved invoice — loads server preview and enables send */
  invoiceId?: number | null;
  /** Draft HTML preview before invoice is saved */
  draftHtml?: string | null;
  defaultEmail?: string;
  onSent?: () => void;
};

export function InvoiceSendModal({
  open,
  onClose,
  invoiceId,
  draftHtml,
  defaultEmail = '',
  onSent,
}: InvoiceSendModalProps) {
  const [detail, setDetail] = useState<FinInvoiceDetail | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [message, setMessage] = useState('');

  const isDraft = !invoiceId && Boolean(draftHtml);
  const canSend = Boolean(invoiceId) && !isDraft;

  useEffect(() => {
    if (!open) return;
    setEmail(defaultEmail);
    setMessage('');
  }, [open, defaultEmail]);

  useEffect(() => {
    if (!open) {
      setDetail(null);
      setPreviewHtml('');
      return;
    }
    if (draftHtml) {
      setPreviewHtml(draftHtml);
      setDetail(null);
      setLoading(false);
      return;
    }
    if (!invoiceId) return;

    let cancelled = false;
    setLoading(true);
    Promise.all([financeApi.getInvoice(invoiceId), financeApi.getInvoicePreviewHtml(invoiceId)])
      .then(([inv, html]) => {
        if (cancelled) return;
        setDetail(inv);
        setPreviewHtml(html);
        setEmail(inv.customerEmail ?? defaultEmail);
      })
      .catch((err) => {
        if (!cancelled) showApiError(err, 'Failed to load invoice preview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, invoiceId, draftHtml, defaultEmail]);

  const handleSend = async () => {
    if (!invoiceId || !email.trim()) {
      notify.error('Enter a recipient email');
      return;
    }
    setSending(true);
    try {
      const result = await financeApi.sendInvoice(invoiceId, {
        email: email.trim(),
        message: message.trim() || undefined,
      });
      notify.success(result.deliveryNote ?? `Invoice sent to ${result.sentTo}`);
      onSent?.();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const title = isDraft
    ? 'Preview draft invoice'
    : canSend
      ? 'Preview & send invoice'
      : 'Invoice preview';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="xl"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            {isDraft ? 'Close' : 'Cancel'}
          </Button>
          {canSend && (
            <Button type="button" loading={sending} onClick={() => void handleSend()}>
              Send email with PDF
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {canSend && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Recipient email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
            />
            <Input
              label="Email message (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Thank you for your business"
            />
          </div>
        )}

        {detail && (
          <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-300 border rounded-lg p-3 bg-slate-50 dark:bg-slate-800/50">
            <span><strong>Invoice:</strong> {detail.invoiceNumber}</span>
            <span><strong>Customer:</strong> {detail.customerName ?? '—'}</span>
            <span><strong>Total:</strong> {detail.totalAmount} {detail.currencyCode}</span>
            <span><strong>Due:</strong> {detail.dueDate ?? '—'}</span>
            {detail.sentTo && <span><strong>Last sent:</strong> {detail.sentTo}</span>}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center">Loading preview…</p>
        ) : previewHtml ? (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white">
            <iframe
              title="Invoice preview"
              srcDoc={previewHtml}
              className="w-full min-h-[420px] border-0 bg-white"
              sandbox=""
            />
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center">No preview available.</p>
        )}

        {isDraft && (
          <p className="text-xs text-slate-500">
            Save the invoice first to send by email. Use &quot;Email customer after creating&quot; on the form to open this send step automatically.
          </p>
        )}
      </div>
    </Modal>
  );
}
